import dayjs from 'dayjs'
import _ from 'lodash'
import { MouseEventHandler, useCallback } from 'react'
import { PiArchive, PiArrowSquareOut, PiCheckSquare, PiNotification } from 'react-icons/pi'
import {
  useMarkNotificationAsArchivedMutation,
  useMarkNotificationAsReadMutation,
  useMarkNotificationAsUnreadMutation,
} from '~/common/generated/apollo-operations'
import {
  actionStepNotificationMetadataSchema,
  getNotificationColorClasses,
  getNotificationIcon,
} from '~/common/utils/notification'
import { apolloClient } from '../../common/apollo-client'
import { Notification } from '../../common/hooks/types'
import { DossSignalType, useDossSignals } from '../../common/hooks/useDossSignals'
import Link from '../shared/Link'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'

interface ActionStepNotificationProps {
  notification: Notification
}

/**
 * Renders an action step notification
 */
export function ActionStepNotification({ notification }: ActionStepNotificationProps) {
  const { dispatch } = useDossSignals()
  const [markNotificationAsRead, { loading: markingAsRead }] = useMarkNotificationAsReadMutation({
    onCompleted: () => {
      apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
      apolloClient.cache.gc()
    },
  })

  const [markNotificationAsUnread, { loading: markingAsUnread }] =
    useMarkNotificationAsUnreadMutation({
      onCompleted: () => {
        apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
        apolloClient.cache.gc()
      },
    })

  const [markNotificationAsArchived, { loading: markingAsArchived }] =
    useMarkNotificationAsArchivedMutation({
      onCompleted: () => {
        apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
        apolloClient.cache.gc()
      },
    })

  const handleToggleRead = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation()
      if (markingAsRead || markingAsUnread) {
        return
      }
      if (notification.isRead) {
        markNotificationAsUnread({ variables: { notificationId: notification.id } })
      } else {
        markNotificationAsRead({ variables: { notificationId: notification.id } })
      }
    },
    [
      notification.id,
      notification.isRead,
      markNotificationAsRead,
      markNotificationAsUnread,
      markingAsRead,
      markingAsUnread,
    ],
  )

  const handleArchival = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation()
      if (markingAsArchived) {
        return
      }
      markNotificationAsArchived({ variables: { notificationId: notification.id } })
    },
    [notification.id, markNotificationAsArchived, markingAsArchived],
  )

  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    if (!notification.isRead && !markingAsRead) {
      markNotificationAsRead({ variables: { notificationId: notification.id } })
    }
    dispatch(DossSignalType.CLOSE_NOTIFICATIONS_PANEL)
  }, [markNotificationAsRead, notification, markingAsRead, dispatch])

  const eventData = actionStepNotificationMetadataSchema.validateSync(notification.metadata)
  const { message, title, notificationType, linkToPath, linkToType } = eventData

  // Get color classes and icon based on notification type
  const colors = getNotificationColorClasses(notificationType)
  const IconComponent = getNotificationIcon(notificationType)

  return (
    <div
      className="group relative flex cursor-pointer items-start border-b border-gray-4 pb-4 pe-3 ps-3 pt-5 hover:bg-gray-1"
      onClick={handleClick}
    >
      <div className="absolute end-5 top-3 hidden items-center gap-0.5 rounded bg-white p-0.5 shadow group-hover:flex">
        <Tooltip enterDelay={20} title={notification.isRead ? 'Mark as unread' : 'Mark as read'}>
          <div className="cursor-pointer rounded p-1 hover:bg-gray-1" onClick={handleToggleRead}>
            {notification.isRead ? <PiNotification size={16} /> : <PiCheckSquare size={16} />}
          </div>
        </Tooltip>
        <Tooltip enterDelay={20} title="Remove notification">
          <div className="cursor-pointer rounded p-1 hover:bg-gray-1" onClick={handleArchival}>
            <PiArchive size={16} />
          </div>
        </Tooltip>
      </div>
      <div className="pt-2">
        <div
          className={`size-2 rounded-[50%] bg-blue-8 ${notification.isRead ? 'invisible' : 'visible'}`}
        ></div>
      </div>
      <div className="flex flex-col px-3">
        <div className="flex items-center gap-2">
          <div
            className={`${colors.bg} ${colors.text} flex size-6 items-center justify-center rounded-full`}
          >
            <IconComponent size={14} />
          </div>
          <div className="text-xs font-semibold">{title}</div>
        </div>
        <div className="flex items-center gap-2 ps-8 pt-1 text-gray-8">
          <div>{dayjs(notification.createdAt).fromNow()}</div>
          {linkToPath && linkToType && (
            <>
              <div className="size-1 rounded-[50%] bg-gray-8"></div>
              <Link
                className="group/link flex items-center gap-2 text-gray-8 hover:text-gray-9"
                href={`/${linkToPath}`}
                noLinkStyle
              >
                <PiArrowSquareOut size={16} />
                <div className="group-hover/link:underline">Go to {_.capitalize(linkToType)}</div>
              </Link>
            </>
          )}
        </div>
        <div className="flex ps-6 pt-1 text-xs">
          <div className="rounded px-2 py-1">{message}</div>
        </div>
      </div>
    </div>
  )
}
